import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const TOAST_ID = 'app-version-check';

/**
 * Polls `/version.json` to detect new deployments while the app is running.
 *
 * On first load, stores the version we booted with. On every subsequent poll,
 * compares the fetched version against the stored one. When a mismatch is
 * detected, a persistent Sonner toast prompts the user to refresh.
 *
 * Uses a stable toast ID so only one notification can exist at a time.
 * Once a mismatch is detected, polling stops — the user either refreshes
 * or dismisses the toast to stay on the current version for this session.
 */
export function useAppVersionCheck() {
  const bootVersion = useRef<string | null>(null);
  const intervalId = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const stopPolling = () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
    };

    const checkVersion = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`);
        if (!res.ok) return;

        const data: { version: string } = await res.json();

        if (!bootVersion.current) {
          bootVersion.current = data.version;
          return;
        }

        if (bootVersion.current !== data.version) {
          stopPolling();
          toast.info('A new version is available', {
            id: TOAST_ID,
            description: `Version ${data.version} is ready — refresh to get the latest features and fixes.`,
            duration: Infinity,
            closeButton: true,
            action: {
              label: 'Refresh',
              onClick: () => window.location.reload(),
            },
            actionButtonStyle: {
              backgroundColor: 'hsl(var(--primary))',
              color: 'hsl(var(--primary-foreground))',
              border: 'none',
            },
          });
        }
      } catch {
        // Version check is non-critical — swallow errors silently
      }
    };

    checkVersion();
    intervalId.current = setInterval(checkVersion, POLL_INTERVAL_MS);
    return stopPolling;
  }, []);
}
